<ng-container *appTranslate="let t" class="h-full">
  <!-- Sized by its timeline rather than by the dashboard grid, but never shorter than a plain tile there
     (two 128px tracks and their gap), so the empty state doesn't leave a sliver of a card -->
  <new-ui-dashboard-card class="[&_.p-card]:min-h-[272px]">
    <ng-template #header>
      <new-ui-dashboard-card-header [title]="t('dashboard.milestone.title')">
        <div class="row items-center gap-2">
          <!-- The empty state means the list has no milestones, which no candidate filter changes -->
          @if (!isEmptyStateShown) {
            <new-ui-candidate-filter [filter]="candidateFilter" (filterChange)="onCandidateFilterChanged($event)" />
            <new-ui-button
              (onClick)="fullscreen = true"
              icon="pi pi-window-maximize"
              severity="secondary"
              [ariaLabel]="t('common.maximize')"
            />
          }
        </div>
      </new-ui-dashboard-card-header>
    </ng-template>

    @if (isEmptyStateShown) {
      <!-- Still offers the other lists, or a list without milestones would leave no way back to them -->
      <ng-container [ngTemplateOutlet]="controls" [ngTemplateOutletContext]="{ $implicit: true }" />

      <ui-emptystate
        class="py-1"
        image="/assets/layout/images/new-ui/book2.png"
        [caption]="t('dashboard.milestone.emptyState.title')"
        [description]="t('dashboard.milestone.emptyState.description')"
        data-testid="milestone-planning-empty-state"
      />
    } @else {
      <ng-container [ngTemplateOutlet]="controls" [ngTemplateOutletContext]="{ $implicit: isCardLoading() }" />

      <!-- Room for two rows of milestones - the renderer's two 220px rows and its 40px margin, which also
         covers two rows plus the summary band - and a scrollbar for any further ones. Scrolls a wrapper
         rather than the view itself, whose tooltip anchor is placed relative to the view's own box. -->
      <div class="max-h-[480px] overflow-y-auto">
        <new-ui-milestone-aggregated-planning-view
          class="block"
          [readonly]="true"
          [milestoneList]="selectedDashboardList()"
          [candidateFilter]="candidateFilter"
          [showProgress]="isProgressShown()"
          [showPlanningData]="isPlanningDataShown()"
          [showSummary]="isSummaryShown()"
          (candidateSum)="onCandidateSumChange($event)"
          (loadingChange)="isCardLoading.set($event)"
        />
      </div>
    }
  </new-ui-dashboard-card>

  <p-dialog
    [(visible)]="fullscreen"
    [modal]="true"
    [style]="{ width: '100vw', height: '100vh', maxHeight: '100vh' }"
    contentStyleClass="flex flex-col"
    appendTo="body"
  >
    <ng-template #header>
      <div class="row w-full items-center justify-between">
        <span>{{ t("dashboard.milestone.title") }}</span>
        <new-ui-candidate-filter [filter]="candidateFilter" (filterChange)="onCandidateFilterChanged($event)" />
      </div>
    </ng-template>

    <ng-container [ngTemplateOutlet]="controls" [ngTemplateOutletContext]="{ $implicit: isDialogLoading() }" />

    @if (fullscreen) {
      <new-ui-milestone-aggregated-planning-view
        class="flex min-h-0 flex-1 flex-col"
        [readonly]="true"
        [milestoneList]="selectedDashboardList()"
        [candidateFilter]="candidateFilter"
        [fullscreen]="true"
        [showProgress]="isProgressShown()"
        [showPlanningData]="isPlanningDataShown()"
        [showSummary]="isSummaryShown()"
        (loadingChange)="isDialogLoading.set($event)"
      />
    }
  </p-dialog>

  <!-- Shared by the card and the fullscreen dialog, both bound to the same state. Each passes whether its own
     timeline is loading, since the card's keeps loading behind the dialog when the filter changes there -->
  <ng-template #controls let-areTogglesHidden>
    <!-- The timeline follows straight after, and its first row of discs begins only a dozen pixels into
       the canvas, so without this the toggles would sit right on top of the rings -->
    <div class="row flex-wrap items-center gap-4 mb-4">
      @if (availableDashboardLists.length > 1) {
        <p-select
          [options]="availableDashboardLists"
          optionLabel="label"
          optionValue="id"
          [ngModel]="selectedDashboardList()?.id"
          [placeholder]="t('processMilestoneList.placeholder')"
          (onChange)="onDashboardListSelected($event.value)"
        />
      }

      <!-- Both toggles only restyle the timeline's rings, so without a list - or before its timeline has
         loaded, or when it has none - there is nothing to toggle -->
      @if (!isNil(selectedDashboardList()) && !areTogglesHidden) {
        @if (canShowProgress()) {
          <label class="flex w-fit cursor-pointer items-center gap-2">
            <p-toggleswitch [(ngModel)]="showProgress" data-testid="switch-milestone-show-progress" />
            <span class="-mt-0.5 text-sm font-medium text-color">{{ t("dashboard.milestone.showProgress") }}</span>
          </label>
        }

        @if (canShowPlanningData()) {
          <label class="flex w-fit cursor-pointer items-center gap-2">
            <p-toggleswitch [(ngModel)]="showPlanningData" data-testid="switch-milestone-show-planning-data" />
            <span class="-mt-0.5 text-sm font-medium text-color">{{ t("dashboard.milestone.showPlanningData") }}</span>
          </label>
        }
      }
    </div>
  </ng-template>
</ng-container>
